import { merchantActionTypes } from '../action-types';
import axios from 'axios';
import { BASE_URL } from '../../../../config/environmentConfig';
import { getFormattedPartnerFields, IPartnerFormattedFields } from '../../../constants/new-partner/new-partner-form';
import { ISelectorData } from '../../../components/application/NewApplication';
import { FORM_FIELDS as APPLICATION_FORM_FIELDS } from '../../../constants/new-application/new-application-form';
import { formikErrorHandler } from '../../../utils/formikUtils';
import { toaster } from 'rsuite';
import toastMessage from '../../../components/ToastMessage';
import { IAction, IPartnerFees, IPartnerList } from '../merchant-interfaces';
import { Dispatch } from 'react';
import { sendSentryMessage } from '../../../utils/sentry-helper';
import * as Sentry from '@sentry/react';
import {
	getFormattedPartnerFeesFields,
	IPartnerFeesReturnTypes,
} from '../../../constants/new-partner/partner-fees-form';
import { GET, PUT } from '../../store-helper';
import { IPartnerFilters } from '../../../components/partners/PartnerListFilters';
import { parseSearchParams } from '../../../utils/utilities';

interface IFetchPartnerList {
	dispatch: Dispatch<IAction>;
	parsedSearch?: IPartnerFilters;
}
export const fetchPartnersList = async ({ dispatch, parsedSearch }: IFetchPartnerList): Promise<void> => {
	try {
		dispatch({ type: merchantActionTypes.SET_PARTNER_LIST_DATA_LOADING, payload: true });

		const data = await GET({
			pathname: `${BASE_URL.MERCHANTS}/partners`,
			params: parsedSearch
		});

		dispatch({ type: merchantActionTypes.FETCH_PARTNER_LIST, payload: data || [] });

	} catch (e) {
		sendSentryMessage({ name: `GET Fetch partners list error`, context: e });
		dispatch({ type: merchantActionTypes.FETCH_PARTNER_LIST, payload: [] });
	}
};

export const setPartnerListDefault = (dispatch: Dispatch<IAction>): void => {
	dispatch({ type: merchantActionTypes.SET_PARTNER_LIST_DEFAULT });
};

export const fetchPartnersListNames = async (dispatch: Dispatch<IAction>): Promise<void> => {
	try {
		dispatch({ type: merchantActionTypes.SET_PARTNER_NAMES_LOADING, payload: true });

		const data = await GET({ pathname: `${BASE_URL.MERCHANTS}/partners` });

		let partnerNamesList = [];

		if (data) {
			partnerNamesList = data?.map(item => ({
				value: item?.id,
				label: item?.name
			}));
		}

		const partnersList = {};

		partnerNamesList?.forEach(el => {
			if (el.value) {
				partnersList[el?.value] = el.label;
			}
		});

		dispatch({ type: merchantActionTypes.SET_PARTNER_NAMES, payload: partnersList });

	} catch (e) {
		sendSentryMessage({ name: `GET Fetch partners list error`, context: e });
		dispatch({ type: merchantActionTypes.FETCH_FAIL });
	}
};

interface IFetchPartnerDetails {
	dispatch: Dispatch<IAction>;
	pid: string;
}

export const fetchPartnerDetails = async ({ dispatch, pid }: IFetchPartnerDetails): Promise<void> => {
	try {
		const data = await GET({ pathname: `${BASE_URL.MERCHANTS}/partners/${pid}` });
		dispatch({ type: merchantActionTypes.FETCH_PARTNER_DETAILS, payload: data || null });
	} catch (e) {
		// sendSentryMessage({ name: `GET Fetch partner details error`, context: e });
		dispatch({ type: merchantActionTypes.FETCH_PARTNER_DETAILS, payload: null });
	}
};

export const setPartnerDetailsDefault = (dispatch: Dispatch<IAction>): void => {
	dispatch({ type: merchantActionTypes.SET_PARTNER_DETAILS_DEFAULT });
};

export const fetchWhiteListedPartnersList = async (dispatch: Dispatch<IAction>): Promise<void> => {
	try {
		dispatch({ type: merchantActionTypes.SET_WHITELABEL_PARTNER_LIST_LOADING, payload: true });

		const data = await GET({ pathname: `${BASE_URL.MERCHANTS}/partners?type=Whitelabel` });

		let partnerNamesList = [];

		if (data?.length) {
			partnerNamesList = data?.map(item => ({
				value: item?.id,
				label: item?.name
			}));
		}

		dispatch({ type: merchantActionTypes.FETCH_WHITELABEL_PARTNER_LIST_SELECTOR, payload: partnerNamesList });
	} catch (e) {
		sendSentryMessage({ name: `GET Fetch partners list error`, context: e });
		dispatch({ type: merchantActionTypes.SET_WHITELABEL_PARTNER_LIST_LOADING, payload: false });
	}
};
export const fetchIntegrationPartnersList = async (dispatch: Dispatch<IAction>): Promise<void> => {
	try {
		dispatch({ type: merchantActionTypes.SET_INTEGRATION_PARTNER_LIST_LOADING, payload: true });

		const data = await GET({ pathname: `${BASE_URL.MERCHANTS}/partners/integration-partners` });

		let partnerNamesList = [];
		if (data) {
			partnerNamesList = data?.map(item => ({
				value: item?.id,
				label: item?.name
			}));
		}
		dispatch({ type: merchantActionTypes.FETCH_INTEGRATION_PARTNER_LIST_SELECTOR, payload: partnerNamesList });
	} catch (e) {
		sendSentryMessage({ name: `GET Fetch partners list error`, context: e });
		dispatch({ type: merchantActionTypes.SET_INTEGRATION_PARTNER_LIST_LOADING, payload: false });
	}
};

export const setNewPartner = async (
	dispatch: Dispatch<IAction>,
	formik: any,
	partner: IPartnerList,
	setChosenPartner: (value: IPartnerList) => void,
	onSuccess: () => void,
	onError: () => void,
	setPartnerList: (value: ISelectorData[] | ((prevValue: ISelectorData[]) => ISelectorData[])) => void,
	partnerChange: (field: string, value: string) => void,
	search: string,
): Promise<void> => {
	dispatch({ type: merchantActionTypes.START_NEW_PARTNER_LOADING });
	if (!formik.isValid) return;
	try {
		const data: IPartnerFormattedFields = getFormattedPartnerFields(formik.values);
		if (partner?.id) {
			data.id = partner.id;
			// delete data.email;
			await axios.put(`${BASE_URL.MERCHANTS}/partners`, data);
		} else {
			const res = await axios.post(`${BASE_URL.MERCHANTS}/partners`, data);
			if (setPartnerList && res?.data) {
				const newPartner: ISelectorData = {
					value: res.data.id,
					label: res.data.name
				};
				setPartnerList(prev => [...prev, newPartner]);
				if (partnerChange) {
					partnerChange(APPLICATION_FORM_FIELDS.PARTNER_ID, res.data.id);
				}
			}
		}

		if (partner?.id) {
			setChosenPartner(null);
		}

		const parsedSearch = parseSearchParams(search) || {};
		await fetchPartnersList({ dispatch, parsedSearch });
		onSuccess();
		dispatch({ type: merchantActionTypes.END_NEW_PARTNER_LOADING });
	} catch (e) {
		sendSentryMessage({ name: `POST Set new partner error`, context: e });
		console.log('error', e);
		if (e.response?.data?.General) {
			dispatch({ type: merchantActionTypes.SET_GENERAL_ERROR, payload: e.response.data.General });
			onError();
		} else if (e.response.data) {
			console.log('here', e.response.data);
			formikErrorHandler({ formik, data: e.response?.data?.errors });
		}
		dispatch({ type: merchantActionTypes.END_NEW_PARTNER_LOADING });
	}
};

export const setPartnerDetails = async (formik, partnerData: IPartnerList, uploadedFile?: string): Promise<void> => {
	const data: IPartnerFormattedFields = getFormattedPartnerFields(formik.values);
	data.id = partnerData?.id;
	// delete data.email;
	if (partnerData?.logoUrl === uploadedFile) {
		data.logoFile = null;
	}
	// if (partnerData?.logoUrl || partnerData?.logoUrl === null) {
	// 	delete data.logoFile;
	// }
	await PUT({
		pathname: `${BASE_URL.MERCHANTS}/partners`,
		body: data,
		formik
	});
};

export const sentPartnerInvite = async (id: string): Promise<void> => {
	try {
		await axios.post((`${BASE_URL.MERCHANTS}/partners/invite?partnerId=${id}`));
		toaster.push(toastMessage({
			text: 'Invitation sent',
			type: 'success'
		}), { placement: 'topEnd' });
	} catch (e) {
		Sentry.setTag('send-partner-invite-id', id);
		sendSentryMessage({ name: `POST Send partner invite error`, context: e });
		console.log(e);
		toaster.push(toastMessage({
			text: e.response?.data?.General?.[0]
				|| e.response?.data?.title
				|| 'Error',
			type: 'error'
		}), { placement: 'topEnd' });
	}
};

export const fetchPartnersFees = async (dispatch: Dispatch<IAction>, id: string): Promise<void> => {
	try {
		dispatch({ type: merchantActionTypes.SET_PARTNER_FEES_LOADING, payload: true });
		const res = await axios.get((`${BASE_URL.MERCHANTS}/partners/${id}/fees`));
		dispatch({ type: merchantActionTypes.FETCH_PARTNER_FEES, payload: res?.data ?? [] });
	} catch (e) {
		dispatch({ type: merchantActionTypes.SET_PARTNER_FEES_LOADING, payload: false });
		console.log('fetchPartnersFees', e);
	}
};

export const setPartnerFees = async (
	dispatch: Dispatch<IAction>,
	formik,
	partnerId: string,
	onSuccess: () => void,
	onError: () => void,
	partnerFeeData?: IPartnerFees,
): Promise<void> => {
	try {
		dispatch({ type: merchantActionTypes.SET_PARTNER_FEES_ADDING_LOADING, payload: true });
		const data: IPartnerFeesReturnTypes = getFormattedPartnerFeesFields(formik.values);
		// if (!data?.appliedOn) {
		// 	delete data?.appliedOn;
		// }
		if (partnerFeeData) {
			data.id = partnerFeeData?.id;
			await axios.put(`${BASE_URL.MERCHANTS}/partners/${partnerId}/fees`, data);
		} else {
			await axios.post(`${BASE_URL.MERCHANTS}/partners/${partnerId}/fees`, data);
		}
		onSuccess();
		dispatch({ type: merchantActionTypes.SET_PARTNER_FEES_ADDING_LOADING, payload: false });
	} catch (e) {
		console.log('setPartnerFees', e?.response);
		dispatch({ type: merchantActionTypes.SET_PARTNER_FEES_ADDING_LOADING, payload: false });
		if (e.response?.data?.errors) {
			formikErrorHandler({ formik, data: e.response?.data?.errors });
		} else {
			onError();
		}
	}
};

export const fetchPartnersBank = async (dispatch: Dispatch<IAction>, id: string): Promise<void> => {
	try {
		dispatch({ type: merchantActionTypes.SET_PARTNER_BANK_LOADING, payload: true });
		const res = await axios.get((`${BASE_URL.MERCHANTS}/partners/${id}/commissions`));
		console.log('res', res);
		dispatch({ type: merchantActionTypes.FETCH_PARTNER_BANK, payload: res?.data?.length ? res?.data : null });
	} catch (e) {
		dispatch({ type: merchantActionTypes.SET_PARTNER_BANK_LOADING, payload: false });
		console.log('fetchPartnersFees', e);
	}
};



export const fetchPartnersListSelector = async (dispatch: Dispatch<IAction>): Promise<void> => {
	try {
		const res = await axios.get(`${BASE_URL.MERCHANTS}/partners/list`);
		let partnersList = [];
		if (res?.data) {
			partnersList = res?.data?.map(el => ({
				value: el?.id,
				label: el?.name,
				role: el?.type
			}));
		}
		dispatch({ type: merchantActionTypes.FETCH_PARTNER_LIST_SELECTOR, payload: partnersList });
	} catch (e) {
		console.log('fetchPartnersListSelector error', e);
	}
};

interface ISetPartnerFilters {
	dispatch: Dispatch<IAction>;
	filters: IPartnerFilters;
}

export const setPartnerFilters = ({ dispatch, filters }: ISetPartnerFilters): void => {
	dispatch({ type: merchantActionTypes.SET_PARTNER_FILTERS, payload: filters });
};

export const setPartnerFiltersToDefault = (dispatch: Dispatch<IAction>): void => {
	const initialFilters: IPartnerFilters = {
		type: '',
		query: ''
	};
	setPartnerFilters({ dispatch, filters: initialFilters });
};